import { z } from "zod";
import { DEFAULT_LANGUAGES } from "@/isomorphic/constants/languages";

export const VirtualSimulationProfileSchema = z.object({
  personaSummary: z.string().nullable().optional(),
  engagementLevel: z.enum(["high", "medium", "low"]).default("medium"),
  responseStyle: z.enum(["brief", "balanced", "detailed"]).default("balanced"),
  caregiverMayAnswer: z.boolean().default(false),
});

export const PatientMetaSchema = z
  .object({
    simulationProfile: VirtualSimulationProfileSchema.nullable().optional(),
  })
  .catchall(z.unknown());

// ============================================================================
// BASE PATIENT SCHEMA (matches backend DbPatientOutput)
// ============================================================================

// To sync with DbPatientOutput in backend/schemas/patient.ts
const BasePatientSchema = z.object({
  // Primary fields
  id: z.string(),

  // Relations
  workspaceId: z.string(),
  creatorId: z.string(),

  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
  archivedAt: z.string().nullable().optional(),

  // Data fields
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  externalId: z.string().nullable().optional(),
  pinCode: z.string().nullable().optional(),
  preferredLanguage: z.enum(DEFAULT_LANGUAGES).default("en-US"),
  meta: PatientMetaSchema.nullable().optional(),
});

export const PatientSchema = BasePatientSchema;

export const PatientListItemSchema = BasePatientSchema.extend({
  status: z.string(), // Calculated: 'active', 'inactive', or 'archived'
  creatorFirstName: z.string().nullable().optional(),
  creatorLastName: z.string().nullable().optional(),
  creatorEmail: z.string().nullable().optional(),
  workspaceName: z.string().nullable().optional(),
});

export const PatientListSchema = z.array(PatientListItemSchema);

// ============================================================================
// CREATE/UPDATE PAYLOAD SCHEMAS (camelCase for API)
// ============================================================================

export const CreatePatientBodySchema = z.object({
  workspaceId: z.string().uuid("Invalid workspace ID"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: z.string().optional(),
  phoneNumber: z.string().optional(),
  email: z.string().email().optional(),
  externalId: z.string().optional(),
  pinCode: z
    .string()
    .regex(/^\d{4}$/, "PIN code must contain exactly 4 digits")
    .optional()
    .or(z.literal("")),
  preferredLanguage: z.enum(DEFAULT_LANGUAGES),
  meta: PatientMetaSchema.optional(),
});

export const UpdatePatientBodySchema = z.object({
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
  dateOfBirth: z.string().optional(),
  phoneNumber: z.string().optional(),
  email: z.string().email().optional(),
  externalId: z.string().optional(),
  pinCode: z
    .string()
    .regex(/^\d{4}$/, "PIN code must contain exactly 4 digits")
    .optional()
    .or(z.literal("")),
  preferredLanguage: z.enum(DEFAULT_LANGUAGES).optional(),
  meta: PatientMetaSchema.optional(),
  archived: z.boolean().optional(), // true = archive, false = unarchive
});

export const DeletePatientBodySchema = z.object({
  deletionReason: z.string().min(1, "Deletion reason is required"),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type CreatePatientBody = z.infer<typeof CreatePatientBodySchema>;
export type UpdatePatientBody = z.infer<typeof UpdatePatientBodySchema>;
export type DeletePatientBody = z.infer<typeof DeletePatientBodySchema>;

// API Response types
export type CreatePatientApiResponse = Patient;
export type UpdatePatientApiResponse = Patient;
export type PatientListApiResponse = z.infer<typeof PatientListSchema>;

export type Patient = z.infer<typeof PatientSchema>;
export type PatientListItem = z.infer<typeof PatientListItemSchema>;
export type VirtualSimulationProfile = z.infer<typeof VirtualSimulationProfileSchema>;
export type PatientMeta = z.infer<typeof PatientMetaSchema>;
